import { useContext, useEffect, useState } from 'react';

import AccountContext from 'contexts/AccountContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';
import { ChartColors } from '../../../../theme/tokens';
import treeMapOptions from '../../highchartOptions/treeMapOptions';
import toBreakdownRows from '../../toBreakdownRows';

interface TreeMapSeriesItemData {
  name: string;
  value: number;
  color: string;
}

interface TreeMapSeriesItem {
  name: string;
  data: TreeMapSeriesItemData[];
  prefix?: PreOrPostFix;
  postfix?: PreOrPostFix;
  precision?: number;
  metricId: string;
}

// Each part in the chart colours, in turn: a tree map colours itself
export const getSeriesData = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
): TreeMapSeriesItemData[] =>
  toBreakdownRows({ data, chartDef }).map((row, index) => ({
    ...row,
    color: ChartColors[index],
  }));

const buildSeries = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
): TreeMapSeriesItem[] => {
  const { metric } = Object.values(data)[0];
  const seriesData = getSeriesData(data, chartDef);

  return [
    {
      name: chartDef.name,
      data: seriesData,
      prefix: metric.formatting.prefix,
      postfix: metric.formatting.postfix,
      precision: metric.formatting.precision,
      metricId: metric.id,
    },
  ];
};

const useTreeMapData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { openMenu } = useContext(DashboardContextMenuContext);

  const [groupField] = useState(() => {
    if (chartDefinition.dimensionA) {
      return chartDefinition.dimensionA.field;
    }
    return 'date';
  });
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const metric = useMetric(
    chartDefinition.series.length > 0
      ? chartDefinition.series[0].metricId
      : undefined,
  );
  const { data } = useV5ChartData(chartDefinition, undefined);
  const { formatMetric } = useValueFormatters();
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'treeMap',
    chartDefinition,
  );

  // Preserved from original: schedule sync on every render
  setTimeout(syncChart);

  useEffect(() => {
    if (!data) {
      return;
    }

    const series = buildSeries(data, chartDefinition);
    const newOptions = {
      ...treeMapOptions({
        title: ' ',
        openMenu,
        groupField,
        fieldType: chartDefinition.dimensionA
          ? chartDefinition.dimensionA.fieldType
          : undefined,
        chartDef: chartDefinition,
        fontSize: 11,
        formatMetric,
      }),
      series: series.map((s: any) => ({
        ...s,
        onDrillDown,
        animation: false,
        dataLabels: {
          enabled: true,
          // @ts-ignore
          formatter: function () {
            // @ts-ignore
            const key = this.key;

            if (metric && chartDefinition.showDataLabels) {
              const formatted = formatMetric({
                // @ts-ignore
                value: this.point.value,
                metricId: metric.id,
              });

              return `<div><span>${key}:</span></div><br/><span>${formatted}</span></div>`;
            }
            return `<div><span>${key}</span></div>`;
          },
        },
      })),
      yAxis: undefined,
      xAxis: undefined,
    };

    setOptions(newOptions as any as Highcharts.Options);
    setIsNoDataToDisplay(
      series.length === 1 &&
        (series[0].data.length === 0 ||
          !series[0].data.some((d) => d.value !== null)),
    );
  }, [
    chartDefinition,
    data,
    formatMetric,
    groupField,
    metric,
    onDrillDown,
    openMenu,
    unitsLocale,
  ]);

  return { options, isNoData: isNoDataToDisplay };
};

export default useTreeMapData;
